import React, { useState, useRef, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Button } from "devextreme-react/button";
import Toolbar, { Item } from "devextreme-react/toolbar";
import {
    DataGrid,
    Column,
    Editing,
    Selection,
    Paging,
    Pager,
    FilterRow,
    HeaderFilter,
    FilterPanel,
    Sorting,
    GroupPanel,
    Grouping,
    Export,
    ColumnChooser,
    ColumnChooserSearch,
    ColumnHeaderFilter,
    StateStoring,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
} from "devextreme-react/data-grid";
import notify from "devextreme/ui/notify";
import FormField from "../../../../dynamicFormBuilder/FormField";
import { GridLayout } from "../../../../common/GridLayout";
import data from "../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../common/Messages";
import { IDCustomerLookupLayout } from "../../../../common/lookups/Layouts/IdLayouts";
import { WMTransactionDPLookupLayout, WMSubLotItemLookupLayout } from "../../../../common/lookups/Layouts/WMLayouts";
import TransferLotsFilterPopup from "../transferLotsToDifferentItems/TransferLotsFilterPopup";
import { TransactionStatus, TransactionStatusDescriptions } from "../../../../enum/Enum";
import "../../../../common/css/searchPage.css";
import "../transferLotsToDifferentItems/transferLots.css";

const TABLE = "WMTRANSACTIONHEADER";

// WMTRANSACTIONHEADER STATUS dropdown — codes + labels from the shared status definitions.
const STATUS_OPTIONS = [
    { code: TransactionStatus.Open, name: TransactionStatusDescriptions.Open },
    { code: TransactionStatus.Hold, name: TransactionStatusDescriptions.Hold },
    { code: TransactionStatus.ReadyToCommitLater, name: TransactionStatusDescriptions.ReadyToCommitLater },
    { code: TransactionStatus.Committed, name: TransactionStatusDescriptions.Committed },
];

const numberFormat = { type: "fixedPoint", precision: 2 };

// On-screen instructions (migrated from the WMD0097 help memo).
const HELP_STEPS = [
    "Enter Customer, Profile, Status, Original Item and Transfer Item.",
    'Click "Find Sublots" to populate the grid with sublots of the Original Item.',
    "Select the lines you want and set the quantity to be transferred.",
    'Click "Start" to process the transfer based on your selections.',
];

const HELP_NOTES = [
    'Both "Original" and "Transfer" items must exist for the selected customer.',
    'Only "SubLot" type items are allowed.',
    'If lots or sublots do not exist for the "Transfer" item, they will be created.',
    "Profile and Status are used when creating the Adjustment transaction.",
    "Each location appears on a separate line, so one sublot may span multiple lines when it exists in multiple locations.",
];

const initialFormFields = {
    R_Customer: "",
    R_Profile: "",
    Status: TransactionStatus.ReadyToCommitLater,
    R_OriginalItem: "",
    R_TransferItem: "",
};

// Header layout. The Original/Transfer item lookups are scoped to the selected customer,
// so the schema is rebuilt whenever the customer changes.
const buildSchema = (customerRowId) => {
    const itemLayout = WMSubLotItemLookupLayout(customerRowId);
    return [
        {
            groupId: 1,
            label: "Transaction",
            fields: [
                { name: "R_Customer", tableName: TABLE, label: "Customer", type: "lookup", layout: IDCustomerLookupLayout({}), required: true, visible: true, isLabelChanged: false },
                { name: "R_Profile", tableName: TABLE, label: "Profile", type: "lookup", layout: WMTransactionDPLookupLayout, required: true, visible: true, isLabelChanged: false },
                { name: "Status", tableName: TABLE, label: "Status", type: "select", options: [], required: true, visible: true, isLabelChanged: false },
            ],
        },
        {
            groupId: 2,
            label: "Items",
            fields: [
                { name: "R_OriginalItem", tableName: TABLE, label: "Original Item", type: "lookup", layout: itemLayout, required: true, visible: true, isLabelChanged: false },
                { name: "R_TransferItem", tableName: TABLE, label: "Transfer Item", type: "lookup", layout: itemLayout, required: true, visible: true, isLabelChanged: false },
            ],
        },
    ];
};

const TransferSubLotsToDifferentItems = (props) => {
    const accessToken = props.oidc.user.access_token;
    const currentUser = props.oidc.user;
    const DYNAMICFORMSETTING = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const formRefs = useRef({});
    const dataGrid = useRef(null);

    const [formFields, setformFields] = useState(initialFormFields);
    const [sublots, setSublots] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [filterVisible, setFilterVisible] = useState(false);
    const [originalItem, setOriginalItem] = useState({ code: "", desc1: "", desc2: "" });

    const headers = { access_token: accessToken, Authorization: `Bearer ${accessToken}` };
    const customerRowId = formFields.R_Customer || "";
    const schema = useMemo(() => buildSchema(customerRowId), [customerRowId]);

    const allowedPageSizes = [10, 20, 100, 500, 1000, "all"];
    const gridKey = "c7e4a3b2-9d18-4f60-8b31-2a95d6f7e1c0";
    const loadState = useCallback(() => GridLayout.sendStorageRequest(gridKey, "GET", accessToken), []);
    const saveState = useCallback((state) => GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state), []);

    const canFindSublots = !!(formFields.R_Customer && formFields.R_Profile && formFields.Status
        && formFields.R_OriginalItem && formFields.R_TransferItem);

    // Load Status options once the form widgets exist.
    const handleFormLoad = useCallback(() => {
        const ref = formRefs.current?.["Status"];
        if (ref && typeof ref.instance === "function") {
            const inst = ref.instance();
            inst.option("dataSource", STATUS_OPTIONS);
            inst.option("valueExpr", "code");
            inst.option("displayExpr", "name");
            if (formFields.Status) inst.option("value", formFields.Status);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFieldChange = useCallback((name, value) => {
        // Items are scoped to the customer — clear them and the grid when it changes.
        if (name === "R_Customer") {
            setformFields((prev) => ({ ...prev, R_OriginalItem: "", R_TransferItem: "" }));
            setSublots([]);
            setSelectedRowKeys([]);
            setOriginalItem({ code: "", desc1: "", desc2: "" });
            return;
        }
        // Immediate feedback (Delphi cdsTransferHeaderR_TransferItemChange): the Transfer Item must
        // differ from the Original Item. Per CLAUDE.md validation convention, an error reverts the
        // value. The server re-checks this at Start as the authoritative guard.
        if (name === "R_TransferItem" && value) {
            setformFields((prev) => {
                if (prev.R_OriginalItem && value === prev.R_OriginalItem) {
                    notify({ message: MESSAGES.TRANSFER_ITEMS_SAME, width: 360 }, "error", 3000);
                    return { ...prev, R_TransferItem: "" };
                }
                return prev;
            });
        }
    }, []);

    // Capture the Original Item's code/descriptions for the filter popup's read-only panel.
    const handleSelectedItemChange = useCallback((name, e) => {
        if (name !== "R_OriginalItem") {
            return;
        }
        const row = e?.selectedItem ?? e;
        if (row && (row.itemcode || row.description1 || row.description2)) {
            setOriginalItem({ code: row.itemcode || "", desc1: row.description1 || "", desc2: row.description2 || "" });
        }
    }, []);

    // Find Sublots opens the WMD0098 filter popup first (optional lot / sublot narrowing).
    const handleFindSublots = () => {
        if (!canFindSublots) {
            return;
        }
        setFilterVisible(true);
    };

    // Runs the actual Find-Sublots query with the optional lot/sublot filter chosen in the popup.
    const runFindSublots = (lotRowId, sublotRowId) => {
        setFilterVisible(false);
        let url = `${window._env_.REACT_APP_APIS}/${data.TransferSubLotsToDifferentItems.GetTransferSubLots}?originalItemRowId=${formFields.R_OriginalItem}`;
        if (lotRowId) {
            url += `&lotRowId=${lotRowId}`;
        }
        if (sublotRowId) {
            url += `&subLotRowId=${sublotRowId}`;
        }
        axios
            .get(url, { headers })
            .then((res) => {
                const raw = res.data?.data ?? res.data ?? [];
                const rows = raw.map((r, idx) => ({ ...r, id: `${r.subLotRowId}_${r.rLocation || idx}` }));
                setSublots(rows);
                setSelectedRowKeys([]);
                if (!rows.length) {
                    notify({ message: MESSAGES.NO_LOTS_FOUND_FOR_ITEM, width: 340 }, "warning", 3000);
                }
            })
            .catch(() => notify({ message: MESSAGES.FETCH_ERROR, width: 320 }, "error", 3000));
    };

    const handleReset = () => {
        setformFields(initialFormFields);
        setSublots([]);
        setSelectedRowKeys([]);
        setOriginalItem({ code: "", desc1: "", desc2: "" });
    };

    // Client-side guard (matches Delphi cdsTransferDetailsTransferQtyChange): Transfer Qty may not
    // exceed Available — warn and cap. The server re-reads the live summary and caps again regardless.
    const handleRowUpdating = (e) => {
        if (e.newData && e.newData.transferQty != null) {
            const available = e.oldData?.available ?? 0;
            if (e.newData.transferQty > available) {
                notify({ message: MESSAGES.TRANSFER_QTY_EXCEEDS_AVAILABLE, width: 360 }, "warning", 3000);
                e.newData.transferQty = available;
            }
        }
    };

    // Selecting a row defaults the Transfer Qty to Available; deselecting resets it to 0.
    const onSelectionChanged = (e) => {
        (e.currentSelectedRowKeys || []).forEach((key) => {
            const row = sublots.find((l) => l.id === key);
            if (row && (!row.transferQty || row.transferQty === 0)) {
                row.transferQty = row.available;
            }
        });
        (e.currentDeselectedRowKeys || []).forEach((key) => {
            const row = sublots.find((l) => l.id === key);
            if (row) {
                row.transferQty = 0;
            }
        });
        setSelectedRowKeys(e.selectedRowKeys);
        if (dataGrid.current) {
            dataGrid.current.instance().repaintRows(
                (e.currentSelectedRowKeys || []).concat(e.currentDeselectedRowKeys || [])
                    .map((k) => sublots.findIndex((l) => l.id === k))
            );
        }
    };

    const handleStart = () => {
        const selectedLines = sublots
            .filter((l) => selectedRowKeys.includes(l.id) && l.transferQty > 0)
            .map((l) => ({ ...l, selected: 1 }));

        if (!selectedLines.length) {
            notify({ message: MESSAGES.NO_RECORDS_SELECTED, width: 340 }, "error", 3000);
            return;
        }

        const payload = {
            rCustomer: formFields.R_Customer,
            rProfile: formFields.R_Profile,
            status: formFields.Status,
            rOriginalItem: formFields.R_OriginalItem,
            rTransferItem: formFields.R_TransferItem,
            lines: selectedLines,
        };

        axios
            .post(`${window._env_.REACT_APP_APIS}/${data.TransferSubLotsToDifferentItems.ProcessTransfer}`, payload, { headers })
            .then((res) => {
                const message = res.data?.message || res.data?.Message || MESSAGES.CREATE_SUCCESS;
                notify({ message, width: 380 }, "success", 4000);
                handleReset();
            })
            .catch((err) => {
                const message = err.response?.data || MESSAGES.UNKNOWN_ERROR_OCCURRED;
                notify({ message, width: 380 }, "error", 4000);
            });
    };

    return (
        <div className="search-page-container">
            <div className="search-page-content">
                <div className="search-page-accordion">
                    <Toolbar className="header">
                        <Item location="before">
                            <div className="title">
                                <h6 style={{ color: window._env_.Color2, marginLeft: "12px" }}>
                                    Transfer Sublots to Different Items
                                </h6>
                            </div>
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" onClick={handleStart} text="Start" type="default" disabled={!selectedRowKeys.length} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" onClick={handleFindSublots} text="Find Sublots" type="default" disabled={!canFindSublots} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" onClick={handleReset} text="Reset" />
                        </Item>
                    </Toolbar>

                    <div className="tl-form-row">
                        <div className="tl-form-col">
                            <FormField
                                key={customerRowId}
                                currrentUser={currentUser}
                                layoutId="A1B2C3D4-5E6F-4708-9A1B-2C3D4E5F6071"
                                layoutName="WMTransferSubLotsToDifferentItemsForm"
                                defaultFormSchema={schema}
                                formFields={formFields}
                                setformFields={setformFields}
                                handleFieldValueChange={handleFieldChange}
                                handleSelectedItemChange={handleSelectedItemChange}
                                DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                                formRefs={formRefs}
                                onFormLoad={handleFormLoad}
                                noSpacingBeforeFirstRow={true}
                            />
                        </div>
                        <aside className="tl-help-panel tl-help-float">
                            <div className="tl-help-body">
                                <div className="tl-help-section">
                                    <div className="tl-help-heading">Steps</div>
                                    <ol className="tl-help-steps">
                                        {HELP_STEPS.map((s, i) => <li key={i}>{s}</li>)}
                                    </ol>
                                </div>
                                <div className="tl-help-section">
                                    <div className="tl-help-heading">Notes</div>
                                    <ul className="tl-help-notes">
                                        {HELP_NOTES.map((n, i) => <li key={i}>{n}</li>)}
                                    </ul>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>

                <TransferLotsFilterPopup
                    visible={filterVisible}
                    itemRowId={formFields.R_OriginalItem}
                    itemCode={originalItem.code}
                    itemDesc1={originalItem.desc1}
                    itemDesc2={originalItem.desc2}
                    onOk={runFindSublots}
                    onCancel={() => setFilterVisible(false)}
                />
                <br />
                <div className="search-page-grid-container">
                    <DataGrid
                        onExporting={(e) => {
                            if (window.handleDevExtremeDataGridExport) {
                                window.handleDevExtremeDataGridExport(e);
                            }
                        }}
                        ref={dataGrid}
                        dataSource={sublots}
                        keyExpr="id"
                        showBorders={true}
                        hoverStateEnabled={true}
                        columnAutoWidth={true}
                        allowColumnReordering={true}
                        allowColumnResizing={true}
                        searchEnabled={true}
                        selectedRowKeys={selectedRowKeys}
                        onSelectionChanged={onSelectionChanged}
                        onRowUpdating={handleRowUpdating}
                        className="search-page-grid"
                    >
                        <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                            <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
                        </ColumnChooser>
                        <ColumnHeaderFilter />
                        <Export enabled={true} allowExportSelectedData={true} />
                        <Selection mode="multiple" showCheckBoxesMode="always" />
                        <Editing mode="cell" allowUpdating={true} />
                        <Sorting mode="multiple" />
                        <Paging defaultPageSize={20} />
                        <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={allowedPageSizes} showNavigationButtons={true} />
                        <FilterPanel visible={true} />
                        <GroupPanel visible={true} />
                        <Grouping autoExpandAll={false} />
                        <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={gridKey} customSave={saveState} />
                        <FilterRow visible={true} />
                        <HeaderFilter visible={true} />

                        <Column dataField="lotCode" caption="Lot" allowEditing={false} />
                        <Column dataField="lotDescription" caption="Lot Description" allowEditing={false} />
                        <Column dataField="subLotCode" caption="SubLot" allowEditing={false} />
                        <Column dataField="subLotDescription" caption="Sublot Description" allowEditing={false} />
                        <Column dataField="warehouse" caption="WH" allowEditing={false} />
                        <Column dataField="location" caption="Location" allowEditing={false} />
                        <Column dataField="available" caption="Available" dataType="number" format={numberFormat} allowEditing={false} />
                        <Column dataField="transferQty" caption="Transfer" dataType="number" format={numberFormat} allowEditing={true} />

                        {/* Hidden technical columns */}
                        <Column dataField="lotRowId" caption="Lot Row Id" visible={false} showInColumnChooser={false} />
                        <Column dataField="subLotRowId" caption="Sublot Row Id" visible={false} showInColumnChooser={false} />
                        <Column dataField="rWarehouse" caption="R Warehouse" visible={false} showInColumnChooser={false} />
                        <Column dataField="rLocation" caption="R Location" visible={false} showInColumnChooser={false} />
                        <Column dataField="locationDesc" caption="Location Description" visible={false} />
                        <Column dataField="inReceiving" caption="In Receiving" dataType="number" format={numberFormat} visible={false} />

                        <GridToolbar>
                            <GridToolbarItem name="exportButton" />
                            <GridToolbarItem name="columnChooserButton" />
                        </GridToolbar>
                    </DataGrid>
                </div>
            </div>
        </div>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(TransferSubLotsToDifferentItems);
